import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, TriangleAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface IntegrationMetric {
  label: string;
  value: ReactNode;
  tone?: 'default' | 'danger';
}

interface IntegrationStatusCardProps {
  title: string;
  description: string;
  icon: ReactNode;
  configured: boolean;
  statusLabel?: string;
  evidence: string;
  metrics: IntegrationMetric[];
  href: string;
  linkLabel: string;
  external?: boolean;
  className?: string;
}

export function IntegrationStatusCard({
  title,
  description,
  icon,
  configured,
  statusLabel,
  evidence,
  metrics,
  href,
  linkLabel,
  external = false,
  className,
}: IntegrationStatusCardProps) {
  const actionClass = 'inline-flex min-h-11 items-center gap-1.5 rounded-lg px-1 text-sm font-semibold text-sky-700 transition-colors hover:text-sky-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2';
  const action = external ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={actionClass}>
      {linkLabel}
      <ChevronRight className="h-4 w-4" aria-hidden="true" />
    </a>
  ) : (
    <Link to={href} className={actionClass}>
      {linkLabel}
      <ChevronRight className="h-4 w-4" aria-hidden="true" />
    </Link>
  );

  return (
    <Card className={cn('flex h-full flex-col border-slate-200 shadow-none', className)}>
      <CardHeader className="space-y-4 p-5 pb-3 sm:p-6 sm:pb-3">
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between sm:gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 ring-1 ring-slate-200">
              {icon}
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-slate-950 sm:text-lg">{title}</h2>
              <p className="mt-0.5 text-sm leading-5 text-slate-500">{description}</p>
            </div>
          </div>
          <Badge
            variant="outline"
            className={cn(
              'shrink-0 gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold sm:self-start',
              configured
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-amber-200 bg-amber-50 text-amber-800',
            )}
          >
            {configured ? <CheckCircle2 className="h-3.5 w-3.5" /> : <TriangleAlert className="h-3.5 w-3.5" />}
            {statusLabel || (configured ? 'Ρυθμισμένο' : 'Θέλει έλεγχο')}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col p-5 pt-2 sm:p-6 sm:pt-2">
        <dl className={cn('grid gap-2', metrics.length >= 3 ? 'grid-cols-3' : 'grid-cols-2')}>
          {metrics.map((metric) => (
            <div key={metric.label} className="min-w-0 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3">
              <dt className="truncate text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">{metric.label}</dt>
              <dd className={cn('mt-1 truncate text-lg font-bold tabular-nums text-slate-900', metric.tone === 'danger' && 'text-rose-700')}>
                {metric.value}
              </dd>
            </div>
          ))}
        </dl>

        <p className="mt-4 text-xs leading-5 text-slate-500">{evidence}</p>
        <div className="mt-auto border-t border-slate-100 pt-3">{action}</div>
      </CardContent>
    </Card>
  );
}
